import { ExprNode, ExprTime, ExprValue, IfNullFn, IsNullFn, KurtFn, SkewFn, StdevFn, StdevPFn, VarFn, VarPFn, ZnFn, } from '@mode-switch/express';
import { Param } from '@mode-switch/model';
import { Relation } from '../Relation';
import { SelectStmt } from '../SelectStmt';
import { Fragment } from '../Fragment';
import { binaryOperandNeedsParens, shouldParenthesizeBinaryLeft, shouldParenthesizeBinaryRight, } from './ParenRules';
export var GenericRelation;
(function (GenericRelation) {
    let Reducer;
    (function (Reducer) {
        function make(api, params) {
            return (initial, node) => {
                switch (node.type) {
                    case "NULL_LITERAL":
                        return addNullLiteral(initial, node);
                    case "BOOLEAN_LITERAL":
                        return addBooleanLiteral(initial, node);
                    case "NUMBER_LITERAL":
                        return addNumberLiteral(initial, node);
                    case "STRING_LITERAL":
                        return addStringLiteral(initial, node);
                    case "REGEXP_LITERAL":
                        return addRegexpLiteral(initial, node);
                    case "DATE_TIME_LITERAL":
                        return addDateTimeLiteral(initial, node);
                    case "LIST_LITERAL":
                        return addListLiteral(initial, node);
                    case "PARAM":
                        return addParamLiteral(initial, node, api, params);
                    case "IDENT":
                        return addIdentifier(initial, node);
                    case "ACCESS":
                        return addAccessExpression(initial, node);
                    case "CALL_NAME":
                        return addCallName(initial, node);
                    case "CALL":
                        return addCallExpression(initial, node, api);
                    case "CAST":
                        return addCastExpression(initial, node, api);
                    case "UNARY":
                        return addUnaryExpression(initial, node, api);
                    case "BINARY":
                        return addBinaryExpression(initial, node, api);
                    case "LOGICAL":
                        return addLogicalExpression(initial, node, api);
                    case "CASE":
                        return addCase(initial, node, api);
                    case "CASE_COND":
                        return addCaseWhen(initial, node, api);
                    case "CASE_ELSE":
                        return addCaseElse(initial, node, api);
                    case "IF_ELSE":
                        return addIfElse(initial, node, api);
                    case "IF_COND":
                        return addIfCond(initial, node, api);
                    case "PROJ":
                        return addProjection(initial, node, api);
                    case "TABLE":
                        return addTable(initial, node);
                    case "FROM":
                        return addFrom(initial, node);
                    case "JOIN":
                        return addJoin(initial, node);
                    case "WHERE":
                        return addWhere(initial, node, api);
                    case "GROUP":
                        return addGroup(initial, node, api);
                    case "HAVING":
                        return addHaving(initial, node, api);
                    case "ORDER":
                        return addOrder(initial, node, api);
                    case "ORDER_TERM":
                        return addOrderTerm(initial, node, api);
                    case "LIMIT":
                        return addLimit(initial, node);
                    case "OFFSET":
                        return addOffset(initial, node);
                    case "CORE":
                        return addCore(initial, node);
                    case "SELECT":
                        return addSelect(initial, node);
                    default:
                        return initial;
                }
            };
        }
        Reducer.make = make;
    })(Reducer = GenericRelation.Reducer || (GenericRelation.Reducer = {}));
    function getFragments(initial, nodes) {
        return nodes.map((node) => Fragment.get(initial, node));
    }
    function getExprFragment(stmt, node, api) {
        return Fragment.getExpr(stmt, node, api);
    }
    function getExprFragments(initial, nodes, api) {
        return nodes.map((node) => getExprFragment(initial, node, api));
    }
    function toBooleanExpr(node, fragment, propagateNulls) {
        fragment = binaryOperandNeedsParens('IS NOT', node, true)
            ? `(${fragment}) IS NOT NULL`
            : `${fragment} IS NOT NULL`;
        if (propagateNulls) {
            return `CASE WHEN ${fragment} THEN TRUE END`;
        }
        else {
            return fragment;
        }
    }
    function requiresCoercionForBooleanExpr(node) {
        switch (getExprDataType(node)) {
            case "FLOAT":
            case "DECIMAL":
            case "INTEGER":
            case "BOOLEAN":
            case "NULL": {
                return false;
            }
            default: {
                return true;
            }
        }
    }
    function getExprDataType(node) {
        switch (node.type) {
            case "CALL_NAME":
            case "IDENT": {
                break;
            }
            default: {
                return node.data.type.name;
            }
        }
    }
    function addNullLiteral(initial, node) {
        return Fragment.set(initial, node, 'NULL');
    }
    function addBooleanLiteral(initial, node) {
        return Fragment.setParam(initial, node, node.value === true ? true : false);
    }
    function addNumberLiteral(initial, node) {
        const { value } = node;
        if (value == null) {
            return Fragment.set(initial, node, 'NULL');
        }
        else {
            return Fragment.setParam(initial, node, ExprValue.asSafeNumberValue(value));
        }
    }
    function addStringLiteral(initial, node) {
        return Fragment.setParam(initial, node, node.value);
    }
    function addRegexpLiteral(initial, node) {
        return Fragment.setParam(initial, node, node.value.toString());
    }
    function addDateTimeLiteral(initial, node) {
        const date = new Date(ExprTime.nodeToMs(node));
        return Fragment.setParam(initial, node, date.toISOString());
    }
    function addListLiteral(initial, node) {
        const fragments = getFragments(initial, node.values);
        return Fragment.set(initial, node, fragments.join(', '));
    }
    function addParamLiteral(initial, node, api, params) {
        const literals = [];
        for (const param of params || []) {
            let values = [];
            if (param.name === node.name) {
                switch (param.value.type) {
                    case "SINGLE": {
                        values = [param.value.value];
                        break;
                    }
                    case "MULTI": {
                        values = param.value.values;
                        break;
                    }
                }
                const reducer = Reducer.make(api, params);
                for (const value of values) {
                    const literal = ExprNode.makeLiteral(value, param.formula.type, param.formula.role);
                    initial = Relation.reduce(initial, literal, reducer);
                    literals.push(literal);
                }
            }
        }
        const fragments = [];
        for (const literal of literals) {
            fragments.push(Fragment.get(initial, literal));
        }
        return Fragment.set(initial, node, `(${fragments.join(', ')})`);
    }
    function addIdentifier(initial, node) {
        const { name } = node;
        return Fragment.set(initial, node, name);
    }
    function addAccessExpression(initial, node) {
        const { path } = node;
        return Fragment.set(initial, node, getFragments(initial, path).join('.'));
    }
    function addCastExpression(initial, node, api) {
        const operand = Fragment.get(initial, node.operand);
        if (operand == null) {
            throw new Error(`Operand to cast cannot be undefined`);
        }
        return Fragment.set(initial, node, api.castValue(operand, node.data.type));
    }
    function addCallName(initial, node) {
        return Fragment.set(initial, node, `${node.name}`);
    }
    function addCallExpression(initial, node, api) {
        const callFragment = Fragment.get(initial, node.callee);
        const argFragments = getExprFragments(initial, node.args, api);
        if (argFragments.some((frag) => typeof frag !== 'string')) {
            throw new Error(`Call arg fragment must be a string`);
        }
        switch (node.call.name) {
            case IfNullFn.Name: {
                const [checkFrag, altFrag] = argFragments;
                if (checkFrag != null && altFrag != null) {
                    return Fragment.set(initial, node, `coalesce(${checkFrag}, ${altFrag})`);
                }
                break;
            }
            case IsNullFn.Name: {
                const valueFragment = argFragments[0];
                if (valueFragment != null) {
                    return Fragment.set(initial, node, `(${binaryOperandNeedsParens('IS', node.args[0], true)
                        ? `(${valueFragment})`
                        : valueFragment} IS NULL)`);
                }
                break;
            }
            case KurtFn.Name: {
                const valueFragment = argFragments[0];
                if (valueFragment != null) {
                    return Fragment.set(initial, node, `kurtosis(${valueFragment})`);
                }
                break;
            }
            case SkewFn.Name: {
                const valueFragment = argFragments[0];
                if (valueFragment != null) {
                    return Fragment.set(initial, node, `skewness(${valueFragment})`);
                }
                break;
            }
            case StdevFn.Name: {
                const valueFragment = argFragments[0];
                if (valueFragment != null) {
                    return Fragment.set(initial, node, `stddev_samp(${valueFragment})`);
                }
                break;
            }
            case StdevPFn.Name: {
                const valueFragment = argFragments[0];
                if (valueFragment != null) {
                    return Fragment.set(initial, node, `stddev_pop(${valueFragment})`);
                }
                break;
            }
            case VarFn.Name: {
                const valueFragment = argFragments[0];
                if (valueFragment != null) {
                    return Fragment.set(initial, node, `var_samp(${valueFragment})`);
                }
                break;
            }
            case VarPFn.Name: {
                const valueFragment = argFragments[0];
                if (valueFragment != null) {
                    return Fragment.set(initial, node, `var_pop(${valueFragment})`);
                }
                break;
            }
            case ZnFn.Name: {
                const valueFragment = argFragments[0];
                if (valueFragment != null) {
                    return Fragment.set(initial, node, `coalesce(${valueFragment}, 0)`);
                }
                break;
            }
        }
        return Fragment.set(initial, node, `${callFragment}(${argFragments.join(', ')})`);
    }
    GenericRelation.addCallExpression = addCallExpression;
    function addUnaryExpression(initial, node, api) {
        const operand = Fragment.getExpr(initial, node.operand, api);
        if (operand == null) {
            throw new Error(`Unary operand cannot be undefined`);
        }
        return Fragment.set(initial, node, `${node.operator}(${operand})`);
    }
    GenericRelation.addUnaryExpression = addUnaryExpression;
    function addBinaryExpression(initial, node, api) {
        const leftFragment = Fragment.getExpr(initial, node.left, api);
        const rightFragment = Fragment.getExpr(initial, node.right, api);
        if (leftFragment == null || rightFragment == null) {
            throw new Error(`Cannot construct backend relation for null operand`);
        }
        switch (node.operator) {
            case '=':
            case '<>': {
                let listOp = 'IN';
                if (node.operator === '<>') {
                    listOp = 'NOT IN';
                }
                const listOpLeftFragment = binaryOperandNeedsParens(listOp, node.left, true)
                    ? `(${leftFragment})`
                    : leftFragment;
                switch (node.right.type) {
                    case "PARAM": {
                        return Fragment.set(initial, node, `${listOpLeftFragment} ${listOp} (${rightFragment})`);
                    }
                    case "LIST_LITERAL": {
                        const list = Fragment.get(initial, node.right);
                        return Fragment.set(initial, node, `${listOpLeftFragment} ${listOp} (${list})`);
                    }
                }
                break;
            }
        }
        return Fragment.set(initial, node, `${shouldParenthesizeBinaryLeft(node) ? `(${leftFragment})` : leftFragment} ${node.operator} ${shouldParenthesizeBinaryRight(node)
            ? `(${rightFragment})`
            : rightFragment}`);
    }
    GenericRelation.addBinaryExpression = addBinaryExpression;
    function addLogicalExpression(initial, node, api) {
        let leftFragment = Fragment.getExpr(initial, node.left, api);
        let rightFragment = Fragment.getExpr(initial, node.right, api);
        if (leftFragment == null) {
            throw new Error(`Left operand of binary expression ${node.operator} cannot be undefined`);
        }
        if (rightFragment == null) {
            throw new Error(`Right operand of binary expression ${node.operator} cannot be undefined`);
        }
        if (requiresCoercionForBooleanExpr(node.left)) {
            leftFragment = toBooleanExpr(node.left, leftFragment, true);
        }
        else if (shouldParenthesizeBinaryLeft(node)) {
            leftFragment = `(${leftFragment})`;
        }
        if (requiresCoercionForBooleanExpr(node.right)) {
            rightFragment = toBooleanExpr(node.right, rightFragment, true);
        }
        else if (shouldParenthesizeBinaryRight(node)) {
            rightFragment = `(${rightFragment})`;
        }
        return Fragment.set(initial, node, `${leftFragment} ${node.operator} ${rightFragment}`);
    }
    GenericRelation.addLogicalExpression = addLogicalExpression;
    function addCase(initial, node, api) {
        const valueFragment = Fragment.getExpr(initial, node.value, api);
        if (valueFragment == null) {
            throw new Error(`Case value ${node.value} cannot be undefined`);
        }
        const condFragments = [];
        for (const cond of node.conds) {
            const condFragment = Fragment.getExpr(initial, cond, api);
            if (condFragment == null) {
                throw new Error(`Case condition value ${node.value} cannot be undefined`);
            }
            condFragments.push(condFragment);
        }
        if (node.else != null) {
            const elseFragment = Fragment.getExpr(initial, node.else, api);
            if (elseFragment == null) {
                throw new Error(`Case value ${node.value} cannot be undefined`);
            }
            return Fragment.set(initial, node, `CASE ${valueFragment} ${condFragments.join(' ')} ${elseFragment} END`);
        }
        return Fragment.set(initial, node, `CASE ${valueFragment} ${condFragments.join(' ')} END`);
    }
    function addCaseWhen(initial, node, api) {
        const whenFragment = Fragment.getExpr(initial, node.when, api);
        const thenFragment = Fragment.getExpr(initial, node.then, api);
        if (whenFragment == null) {
            throw new Error(`Case condition when ${node.when} cannot be undefined`);
        }
        if (thenFragment == null) {
            throw new Error(`Case condition then ${node.when} cannot be undefined`);
        }
        return Fragment.set(initial, node, `WHEN ${whenFragment} THEN ${thenFragment}`);
    }
    function addCaseElse(initial, node, api) {
        const thenFragment = Fragment.getExpr(initial, node.then, api);
        if (thenFragment == null) {
            throw new Error(`Case condition then ${node.then} cannot be undefined`);
        }
        return Fragment.set(initial, node, `ELSE ${thenFragment}`);
    }
    function addIfElse(initial, node, api) {
        const baseFragment = Fragment.getExpr(initial, node.base, api);
        if (baseFragment == null) {
            throw new Error(`If/else base condition ${node.base} cannot be undefined`);
        }
        const condFragments = [];
        for (const cond of node.conds) {
            const condFragment = Fragment.getExpr(initial, cond, api);
            if (condFragment == null) {
                throw new Error(`If/else condition ${cond} cannot be undefined`);
            }
            condFragments.push(condFragment);
        }
        if (node.else != null) {
            const elseFragment = Fragment.getExpr(initial, node.else, api);
            if (elseFragment == null) {
                throw new Error(`If/else else ${node.else} cannot be undefined`);
            }
            if (condFragments.length === 0) {
                return Fragment.set(initial, node, `CASE ${baseFragment} ELSE ${elseFragment} END`);
            }
            else {
                return Fragment.set(initial, node, `CASE ${baseFragment} ${condFragments.join(' ')} ELSE ${elseFragment} END`);
            }
        }
        if (condFragments.length === 0) {
            return Fragment.set(initial, node, `CASE ${baseFragment} END`);
        }
        else {
            return Fragment.set(initial, node, `CASE ${baseFragment} ${condFragments.join(' ')} END`);
        }
    }
    function addIfCond(initial, node, api) {
        let whenFragment = Fragment.getExpr(initial, node.cond, api);
        const thenFragment = Fragment.getExpr(initial, node.result, api);
        if (whenFragment == null) {
            throw new Error(`If/else base condition ${node.cond} cannot be undefined`);
        }
        if (thenFragment == null) {
            throw new Error(`If/else base result ${node.result} cannot be undefined`);
        }
        if (requiresCoercionForBooleanExpr(node.cond)) {
            whenFragment = toBooleanExpr(node.cond, whenFragment, false);
        }
        return Fragment.set(initial, node, `WHEN ${whenFragment} THEN ${thenFragment}`);
    }
    function addProjection(initial, node, api) {
        let fragment = Fragment.getExpr(initial, node.expr, api);
        if (fragment == null) {
            throw new Error(`Error creating select statement`);
        }
        if (node.alias) {
            const aliasFrag = Fragment.get(initial, node.alias);
            if (aliasFrag != null) {
                fragment += ` AS ${aliasFrag}`;
            }
        }
        return SelectStmt.addProjection(Fragment.set(initial, node, fragment), fragment);
    }
    GenericRelation.addProjection = addProjection;
    function addTable(initial, node) {
        const fragment = Fragment.get(initial, node.name);
        if (fragment == null) {
            throw new Error(`Error adding table to select statement`);
        }
        return SelectStmt.addProjection(Fragment.set(initial, node, fragment), fragment);
    }
    GenericRelation.addTable = addTable;
    function addFrom(initial, node) {
        switch (node.source.type) {
            case "SELECT":
            case "CORE": {
                const subFrag = Fragment.get(initial, node.source);
                const aliasFrag = Fragment.get(initial, node.alias);
                if (subFrag == null || aliasFrag == null) {
                    throw new Error(`Error adding from to select statement`);
                }
                const fragment = `FROM (${subFrag}) AS ${aliasFrag}`;
                return SelectStmt.addSelect(Fragment.set(initial, node, fragment), subFrag);
            }
            case "TABLE": {
                const tableFrag = Fragment.get(initial, node.source);
                const aliasFrag = Fragment.get(initial, node.alias);
                if (tableFrag == null || aliasFrag == null) {
                    throw new Error(`Error adding from to select statement`);
                }
                let fragment = `FROM ${tableFrag} AS ${aliasFrag}`;
                if (tableFrag === aliasFrag) {
                    fragment = `FROM ${tableFrag}`;
                }
                return SelectStmt.addSelect(Fragment.set(initial, node, fragment), tableFrag);
            }
        }
    }
    GenericRelation.addFrom = addFrom;
    function addJoin(initial, node) {
        const sourceFrag = Fragment.get(initial, node.source);
        const aliasFrag = Fragment.get(initial, node.alias);
        const predFrag = Fragment.get(initial, node.predicate);
        if (sourceFrag == null || aliasFrag == null || predFrag == null) {
            throw new Error('Error adding join to select statement');
        }
        switch (node.source.type) {
            case "SELECT":
            case "CORE": {
                const fragment = `${node.operator} JOIN (${sourceFrag}) AS ${aliasFrag} ON ${predFrag}`;
                return SelectStmt.addJoin(Fragment.set(initial, node, fragment), fragment);
            }
            case "TABLE": {
                let fragment = `${node.operator} JOIN ${sourceFrag} AS ${aliasFrag} ON ${predFrag}`;
                if (sourceFrag === aliasFrag) {
                    fragment = `${node.operator} JOIN ${sourceFrag} ON ${predFrag}`;
                }
                return SelectStmt.addJoin(Fragment.set(initial, node, fragment), fragment);
            }
        }
    }
    GenericRelation.addJoin = addJoin;
    function addWhere(initial, node, api) {
        if (node.exprs.length > 0) {
            const exprs = getExprFragments(initial, node.exprs, api);
            let fragment = `WHERE (${exprs.join(') AND (')})`;
            if (node.exprs.length === 1) {
                fragment = `WHERE ${exprs.join('')}`;
            }
            return SelectStmt.addWhere(Fragment.set(initial, node, fragment), fragment);
        }
        return initial;
    }
    GenericRelation.addWhere = addWhere;
    function addGroup(initial, node, api) {
        if (node.exprs.length > 0) {
            const exprs = getExprFragments(initial, node.exprs, api);
            const fragment = `GROUP BY ${exprs.join(', ')}`;
            return SelectStmt.addGroup(Fragment.set(initial, node, fragment), fragment);
        }
        return initial;
    }
    GenericRelation.addGroup = addGroup;
    function addHaving(initial, node, api) {
        if (node.exprs.length > 0) {
            const exprs = getExprFragments(initial, node.exprs, api);
            let fragment = `HAVING (${exprs.join(') AND (')})`;
            if (node.exprs.length === 1) {
                fragment = `HAVING ${exprs.join('')}`;
            }
            return SelectStmt.addHaving(Fragment.set(initial, node, fragment), fragment);
        }
        return initial;
    }
    GenericRelation.addHaving = addHaving;
    function addOrder(initial, node, api) {
        if (node.terms.length > 0) {
            const exprs = getFragments(initial, node.terms);
            const fragment = `ORDER BY ${exprs.join(', ')}`;
            return SelectStmt.addOrder(Fragment.set(initial, node, fragment), fragment);
        }
        return initial;
    }
    GenericRelation.addOrder = addOrder;
    function addOrderTerm(initial, node, api) {
        let order;
        switch (node.order) {
            case "DESCENDING":
                order = 'DESC';
                break;
            case "ASCENDING":
            default:
                order = 'ASC';
                break;
        }
        const expr = `${getExprFragment(initial, node.expr, api)} ${order}`;
        return SelectStmt.addOrder(Fragment.set(initial, node, expr), expr);
    }
    GenericRelation.addOrderTerm = addOrderTerm;
    function addLimit(initial, node) {
        const fragment = `LIMIT ${Fragment.get(initial, node.expr)}`;
        return SelectStmt.setLimit(Fragment.set(initial, node, fragment), fragment);
    }
    GenericRelation.addLimit = addLimit;
    function addOffset(initial, node) {
        const fragment = `OFFSET ${Fragment.get(initial, node.expr)}`;
        return SelectStmt.setOffset(Fragment.set(initial, node, fragment), fragment);
    }
    GenericRelation.addOffset = addOffset;
    function addCore(initial, node) {
        let fragment = `SELECT`;
        if (node.projections != null) {
            fragment += `\n${getFragments(initial, node.projections || []).join(',\n')}`;
        }
        if (node.from != null) {
            fragment += `\n${Fragment.get(initial, node.from)}`;
        }
        if (node.joins != null) {
            fragment += `\n${getFragments(initial, node.joins).join('\n')}`;
        }
        if (node.where != null && node.where.exprs.length > 0) {
            fragment += `\n${Fragment.get(initial, node.where)}`;
        }
        if (node.group != null && node.group.exprs.length > 0) {
            fragment += `\n${Fragment.get(initial, node.group)}`;
        }
        if (node.having != null && node.having.exprs.length > 0) {
            fragment += `\n${Fragment.get(initial, node.having)}`;
        }
        return Fragment.set(initial, node, fragment);
    }
    GenericRelation.addCore = addCore;
    function addSelect(initial, node) {
        let fragment = `${getFragments(initial, node.cores).join(' UNION ALL ')}`;
        if (node.order != null && node.order.terms.length > 0) {
            fragment += `\n${Fragment.get(initial, node.order)}`;
        }
        if (node.limit != null) {
            fragment += `\n${Fragment.get(initial, node.limit)}`;
        }
        if (node.offset != null) {
            fragment += `\n${Fragment.get(initial, node.offset)}`;
        }
        return Fragment.set(initial, node, fragment);
    }
    GenericRelation.addSelect = addSelect;
})(GenericRelation || (GenericRelation = {}));
